/**
 * Modifier le texte d'une narration de l'animation dans l'écran « Ta voix » :
 * l'accroche de la vidéo (texte, mot en or, ce que dit la voix) et la narration
 * de chaque scène (sous-titres, ce que dit la voix). L'intro et la fin de la
 * vidéo sont écrites par le lecteur : elles ne se modifient pas.
 *
 * Si ce que dit la voix change, la voix enregistrée ne colle plus : elle est
 * retirée (à réenregistrer). Pur, sur le brouillon BRUT (comme `withVoice`).
 */

import { withVoice, type VoiceTarget } from '@jeraisonne/animation';

export interface NarrationEdit {
  text: string;
  /** ce que dit la voix ; vide = le texte */
  say?: string;
  /** accroche seulement : mot ou groupe de mots en or ; vide = aucun */
  emphasis?: string;
}

type Json = Record<string, unknown>;
const isRecord = (v: unknown): v is Json =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

export function isEditableTarget(target: VoiceTarget): boolean {
  return target.kind === 'scene' || target.kind === 'hook';
}

/** Ce que dit la voix (le texte si rien de précisé). */
function spoken(holder: Json): string {
  const say = typeof holder.say === 'string' ? holder.say.trim() : '';
  return say || (typeof holder.text === 'string' ? holder.text.trim() : '');
}

export function withNarrationText(
  draft: unknown,
  target: VoiceTarget,
  edit: NarrationEdit,
): { draft: Json; voiceRemoved: boolean } {
  if (!isRecord(draft)) throw new Error('brouillon illisible');
  if (!isEditableTarget(target)) throw new Error('ce texte n’est pas modifiable');
  let doc = structuredClone(draft);
  const text = edit.text.trim();
  const say = edit.say?.trim() ?? '';

  let holder: Json;
  let voiceKey: string;
  if (target.kind === 'scene') {
    const scene = (Array.isArray(doc.scenes) ? doc.scenes : []).find(
      (s): s is Json => isRecord(s) && s.id === target.sceneId,
    );
    if (!scene || !isRecord(scene.narration))
      throw new Error(`scène « ${target.sceneId} » introuvable`);
    holder = scene.narration;
    voiceKey = 'audioAssetId';
  } else {
    if (!isRecord(doc.bookends)) doc.bookends = {};
    const bookends = doc.bookends as Json;
    if (!isRecord(bookends.hook)) bookends.hook = { text };
    holder = bookends.hook as Json;
    voiceKey = 'hookAudioAssetId';
  }

  const before = spoken(holder);
  holder.text = text;
  if (say && say !== text) holder.say = say;
  else delete holder.say;
  if (target.kind === 'hook') {
    const emphasis = edit.emphasis?.trim() ?? '';
    if (emphasis && text.toLowerCase().includes(emphasis.toLowerCase())) holder.emphasis = emphasis;
    else delete holder.emphasis;
  }

  const voiceHolder = target.kind === 'scene' ? holder : (doc.bookends as Json);
  const hadVoice = typeof voiceHolder[voiceKey] === 'string';
  const voiceRemoved = hadVoice && spoken(holder) !== before;
  if (voiceRemoved) doc = withVoice(doc, target, null);
  return { draft: doc, voiceRemoved };
}
